/**
 * Two-word bindings (D242; the Meridian's §13.17) — `hold the shell UP TO
 * the window`, where the verb declares `up to` as one preposition.
 *
 * @remarks
 * A binding was one word until D242. English has prepositions that are two
 * (`up to`, `out of`, `next to`), and a player who holds a thing UP TO a
 * window has said exactly what `hold … to` says, with a word more; read one
 * word at a time, `up` is a direction that demotes into the object's phrase
 * ("shell up") and the object is never found.
 *
 * So a verb may declare a binding of TWO words, and the pair is read as one
 * preposition in that verb's clause — the positional rule every binding
 * keeps (M1 discovery 3): the HEAD intent is the authority on its own words,
 * and only its own. `hold the shell up to the window` binds `up to` because
 * `hold` (the Meridian's `use`) declares it; `go up to the deck` is the
 * movement verb with a direction, because `go` declares nothing of the kind.
 *
 * The merge runs after canonicalization, on the canonical stream, so the
 * structure matcher sees one word token whose text is the binding and looks
 * it up exactly as it looks up `to`. Its span covers both words, so an echo
 * points at what was typed. Two words only — the compiler refuses three — and
 * never across a verb or a pronoun: a binding sits between two phrases.
 *
 * @packageDocumentation
 */

import { type CanonicalToken } from './canonicalize-tokens.js';

/**
 * Is this canonical token one a two-word binding can be built from — a plain
 * word, or a direction (`up`, `out`), which demotes into a binding as it
 * demotes into a phrase?
 *
 * @param token - The token, or nothing past the clause's end
 * @returns Whether it can be half of a binding
 */
const isBindingMaterial = (token: CanonicalToken | undefined): token is Extract<CanonicalToken, { kind: 'direction' | 'word' }> =>
	token !== undefined && (token.kind === 'word' || token.kind === 'direction');

/**
 * Read every adjacent pair the head intent declares as a two-word binding as
 * one word token carrying the binding's text.
 *
 * @param tokens - One clause's canonical tokens
 * @returns The tokens, each declared pair merged (the same array when the head declares none, or none is typed)
 */
export const mergeTwoWordPrepositions = (tokens: readonly CanonicalToken[]): readonly CanonicalToken[] => {
	const [head] = tokens;

	if (head?.kind !== 'intent' || ![...head.intent.prepositionRoles.keys()].some((preposition) => preposition.includes(' '))) {
		return tokens;
	}

	const merged: CanonicalToken[] = [];
	// A token the pair before it consumed is skipped, as the multiword-alias walk skips by count.
	let isConsumed = false;

	for (const [tokenIndex, token] of tokens.entries()) {
		if (isConsumed) {
			isConsumed = false;
			continue;
		}

		const next = tokens[tokenIndex + 1];
		const pairText = isBindingMaterial(token) && isBindingMaterial(next) ? `${token.sourceText} ${next.sourceText}` : undefined;

		if (pairText !== undefined && next !== undefined && head.intent.prepositionRoles.has(pairText)) {
			merged.push({ kind: 'word', sourceSpan: { end: next.sourceSpan.end, start: token.sourceSpan.start }, sourceText: pairText });
			isConsumed = true;
			continue;
		}

		merged.push(token);
	}

	return merged;
};
